import { useCallback, useContext, useState } from "react";

import { GetAccount200ResponsePositionsInner } from "../../../../codegen-api";
import AdjustIsolatedMarginModal from "../../../../components/ConfirmationModal/AdjustIsolatedMarginModal";
import ClosePositionModal from "../../../../components/ConfirmationModal/ClosePositionModal";
import TPSLForPositionModal from "../../../../components/ConfirmationModal/TPSLForPositionModal";
import { PnLCardModal } from "../../../../components/PnLCardModal";
import PositionDetailsModal from "../../../../components/shared/ViewDetailsModal/PositionDetailsModal";
import { MarketInstrumentContext } from "../../../../contexts/MarketInstrumentContext";
import { useGetAccount } from "../../../../hooks/api/account/useGetAccount";
import { useOrder } from "../../../../hooks/api/order/useOrder";
import { useRFQ } from "../../../../hooks/api/rfq/useRFQ";
import MobilePositionRow from "./MobilePositionRow";
import { IMobilePositionsProps } from "./types";

function MobilePositions({ positions, t }: IMobilePositionsProps) {
  const { getMarketPrecision } = useContext(MarketInstrumentContext);
  const { data: accountData } = useGetAccount();
  // TP/SL only exists where a trigger executor is deployed (Monad + Arc today).
  const { tpslSupported } = useOrder();

  // NOTE: IDs here are referring the position's instrument_id
  const [shareModalPositionId, setShareModalPositionId] = useState<string>();
  const [closingPositionId, setClosingPositionId] = useState<string>();
  const [viewDetailsForPositionId, setViewDetailsForPositionId] =
    useState<string>();
  const [adjustingMarginPositionId, setAdjustingMarginPositionId] =
    useState<string>();
  const [showClosePositionModal, setShowClosePositionModal] =
    useState<boolean>(false);
  const [tpslModalPositionId, setTpslModalPositionId] = useState<string>();

  const { deleteRFQsByInstrumentId } = useRFQ(undefined, false);

  const onClosePosition = useCallback(
    (pos: GetAccount200ResponsePositionsInner) => {
      setClosingPositionId(pos.instrument_id);
      setShowClosePositionModal(true);
    },
    []
  );

  const onClosePositionModalHide = useCallback(() => {
    if (closingPositionId) {
      deleteRFQsByInstrumentId(closingPositionId);
    }

    setShowClosePositionModal(false);
  }, [closingPositionId, deleteRFQsByInstrumentId]);

  const positionByInstrumentId = useCallback(
    (instrumentId?: string) => {
      if (!instrumentId) {
        return undefined;
      }

      return positions.find(
        (position) => position.instrument_id === instrumentId
      );
    },
    [positions]
  );

  const onAdjustMarginModalHide = useCallback(() => {
    setAdjustingMarginPositionId(undefined);
  }, []);

  const onPositionDetailsModalHide = useCallback(() => {
    setViewDetailsForPositionId(undefined);
  }, []);

  const onSharePositionModalHide = useCallback(() => {
    setShareModalPositionId(undefined);
  }, []);

  const onTPSLForPositionModalHide = useCallback(() => {
    setTpslModalPositionId(undefined);
  }, []);

  return (
    <>
      <AdjustIsolatedMarginModal
        position={positionByInstrumentId(adjustingMarginPositionId)}
        onHide={onAdjustMarginModalHide}
      />
      <ClosePositionModal
        position={positionByInstrumentId(closingPositionId)}
        onHide={onClosePositionModalHide}
        show={showClosePositionModal}
      />
      <PositionDetailsModal
        position={positionByInstrumentId(viewDetailsForPositionId)}
        onHide={onPositionDetailsModalHide}
      />
      <PnLCardModal
        pnlType="position"
        pnlData={positionByInstrumentId(shareModalPositionId)}
        onHideModal={onSharePositionModalHide}
        show={!!shareModalPositionId}
        onHidePnLCard={onSharePositionModalHide}
        hideBackButton
      />
      <TPSLForPositionModal
        position={positionByInstrumentId(tpslModalPositionId)}
        onHide={onTPSLForPositionModalHide}
        show={!!tpslModalPositionId}
      />
      <div>
        {positions.map((position, i) => {
          const { price_precision, amount_precision } = getMarketPrecision(
            position.asset,
            position.instrument_type
          );
          return (
            <MobilePositionRow
              key={position.instrument_id}
              position={position}
              i={i}
              positionsLength={positions.length}
              price_precision={price_precision}
              amount_precision={amount_precision}
              accountData={accountData}
              t={t}
              onClosePosition={onClosePosition}
              setAdjustingMarginPositionId={setAdjustingMarginPositionId}
              setViewDetailsForPositionId={setViewDetailsForPositionId}
              setShareModalPositionId={setShareModalPositionId}
              setTpslModalPositionId={
                tpslSupported ? setTpslModalPositionId : undefined
              }
            />
          );
        })}
      </div>
    </>
  );
}

export default MobilePositions;
